import type { ComposerWidget } from '@safetyhub/shared';

/**
 * 작성 탭 위젯 붙여넣기의 **경로 판정**(순수 함수) — 이동(cut 버퍼) vs payload 채널.
 *
 * EditorPage.handleWidgetPaste 인라인에 있던 판정을 그대로 옮긴 것으로, 분리 이유는 이 판정에서만
 * 같은 유형 결함이 세 번 났고(아래 F1/F2/F4) 증거가 사람 실측뿐이었기 때문이다. 여기 있는 규칙이
 * 무너지면 "다른 위젯이 붙거나 / orphan이 생기거나 / 다른 템플릿에 얹히는" 조용한 데이터 오염이라,
 * 회귀 가드를 코드로 고정한다(widgetPasteRoute.test.ts).
 */

/** 잘라내기 버퍼 — 잘라낸 위젯 스냅샷 + **소유 템플릿 key**(이동 성립 범위). */
export interface WidgetCutBuffer {
  widgets: ComposerWidget[];
  /** 잘라낸 시점의 템플릿 key — 붙여넣기 대상이 같은 템플릿일 때만 이동 경로를 태운다. */
  templateKey: string;
}

/**
 * 붙여넣기 경로 판정 결과(판별 union).
 *  - `move`: cut 버퍼의 스냅샷을 **무재발급으로 원본 삽입**(기존 이동 시맨틱).
 *  - `payload`: OS 클립보드/localStorage payload 채널(클론·키 재발급·의존 이식). `triggerTemplateKey`는
 *    비동기 readText resolve 이후의 경합 대조 기준(F4).
 */
export type WidgetPasteRoute =
  | { kind: 'move'; widgets: ComposerWidget[] }
  | { kind: 'payload'; triggerTemplateKey: string };

/**
 * 이동 경로는 **잘라낸 그 템플릿 안에서만** 성립한다.
 *
 * - F1(cut 버퍼 수명): 붙여넣기가 cut 버퍼를 **무조건 우선**하므로, 새 복사가 버퍼를 비우지 않으면
 *   "A 잘라내기 → B 복사 → Ctrl+V"에서 A가 붙는다. 버퍼를 비우는 것은 handleWidgetCopy의 책임이고,
 *   이 함수는 그 계약의 반대쪽 — `cut == null`이면 **반드시 payload**임을 보장한다.
 * - F2(템플릿 전환): EditorPage는 템플릿 전환에 언마운트되지 않아 버퍼가 살아남는다. key가 다른데
 *   이동을 태우면 근거 body 노드·의존 데이터가 없는 곳에 orphan으로 꽂힌다. → payload로 폴백
 *   (payload는 sourceTemplateKey로 타 템플릿 이식을 이미 처리). 버퍼는 호출부가 남겨 둔다(원래
 *   템플릿으로 돌아오면 이동이 그대로 살아 있게).
 */
export function resolveWidgetPasteRoute(
  cut: WidgetCutBuffer | null,
  currentTemplateKey: string,
): WidgetPasteRoute {
  if (cut && cut.widgets.length > 0 && cut.templateKey === currentTemplateKey)
    return { kind: 'move', widgets: cut.widgets };
  return { kind: 'payload', triggerTemplateKey: currentTemplateKey };
}

/**
 * payload 붙여넣기 경합 가드(F4) — `navigator.clipboard.readText()`는 비동기라, resolve 전에 사용자가
 * 템플릿을 바꾸면 클로저(트리거 시점 template/tree 기준 계산)가 **다른 템플릿에 얹힌다**. 트리거 시점
 * key와 dispatch 시점 key가 다르면 stale — 호출부는 조용히 실패하지 말고 안내 후 중단해야 한다.
 */
export function isWidgetPasteDispatchStale(
  route: Extract<WidgetPasteRoute, { kind: 'payload' }>,
  currentTemplateKey: string,
): boolean {
  return route.triggerTemplateKey !== currentTemplateKey;
}
